import {useEffect, useReducer, useRef} from "react";

import swapFormStateReducer from "../swapFormStateReducer";
import usePreviousValue from "../../../../core/util/hook/usePrevious";
import {isValidAssetID, orderByAssetId} from "../../../../asset/util/assetUtils";
import SwapError from "../../../error/SwapError";
import {initialSwapFormState} from "../../context/SwapFormContext.constants";
import {generateSwapFormAssetData} from "../../SwapForm.utils";

export type UseSwapFormHookOptions = {
  assetInID: number | null;
  assetOutID: number | null;
  onError?: (error: SwapError) => void;
};

function useSwapForm({assetInID, assetOutID, onError}: UseSwapFormHookOptions) {
  const [state, dispatch] = useReducer(swapFormStateReducer, initialSwapFormState);
  const previousAssetIDs = usePreviousValue({assetInID, assetOutID});
  const onErrorRef = useRef(onError);

  useEffect(() => {
    onErrorRef.current = onError;
  }, [onError]);

  useEffect(() => {
    // check if the assets were changed and reset the form
    // do not reset state if the FLIP function was used to change the assets

    if (
      isValidAssetID(previousAssetIDs?.assetInID) &&
      isValidAssetID(previousAssetIDs?.assetOutID) &&
      isValidAssetID(assetInID) &&
      isValidAssetID(assetOutID)
    ) {
      const serializedPrevAssetIDS = orderByAssetId([
        {id: previousAssetIDs!.assetInID},
        {id: previousAssetIDs!.assetOutID}
      ])
        .map((asset) => asset.id)
        .join("-");
      const newAssetIDs = orderByAssetId([{id: assetInID}, {id: assetOutID}])
        .map((asset) => asset.id)
        .join("-");

      if (serializedPrevAssetIDS !== newAssetIDs) {
        dispatch({
          type: "RESET_FORM"
        });
      }
    }
  }, [assetInID, assetOutID, previousAssetIDs]);

  useEffect(() => {
    dispatch({type: "SET_IS_READY", isReady: false});

    generateSwapFormAssetData({assetIN: assetInID, assetOUT: assetOutID})
      .then((data) => {
        dispatch({
          type: "SET_SWAP_FORM_ASSETS",
          assetIn: data.assetIn,
          assetOut: data.assetOut
        });
      })
      .catch((error) => {
        if (onErrorRef.current) {
          onErrorRef.current(
            new SwapError(error.type || "FailedToFetchAssetDetails", error.message)
          );
        }
      });
  }, [assetInID, assetOutID]);

  return {
    state,
    dispatch
  };
}

export default useSwapForm;
